import React from "react";
import OurInvestorsImage from "../icons/our_investors_image.svg";
import ExposureImage from "../icons/exposure_image.svg";

export const overviewFaq = [
  {
    title: "Buy & Sell Private Shares with Other Investors",
    description: (
      <p className="ml-8">
        The BnkToTheFuture Secondary Market gives verified investors the ability
        to trade private equity shares securely. Buyers and sellers set their
        own pricing, enabling peer-to-peer trading opportunities beyond the
        original funding round.
      </p>
    ),
  },
  {
    title: "How it works",
    description: (
      <ol className="list-decimal pl-4 space-y-1 ml-8">
        <li>
          <strong>Verify & Certify</strong> – Complete your investor
          verification.
        </li>
        <li>
          <strong>Place an Order</strong> – Choose an existing offer or create
          your own buy/sell order with a custom price.
        </li>
        <li>
          <strong>Pre-Sign Agreement</strong> – Review and pre-sign a legally
          binding contract for execution once matched.
        </li>
        <li>
          <strong>Settlement & Share Transfer</strong> – Once matched, payment
          is processed and your portfolio is automatically updated.
        </li>
      </ol>
    ),
  },
  {
    title: "Why Use the Secondary Market?",
    description: (
      <div className="space-y-2 ml-8">
        <div>
          <p>
            <strong>Buyers:</strong> Access opportunities in companies no longer
            accepting new funding, increase your stake, or find discounted deals
            from early investors.
          </p>
        </div>
        <div>
          <p>
            <strong>Sellers:</strong> Exit positions before a potential IPO or
            acquisition, rebalance your portfolio, and access early liquidity.
          </p>
        </div>
      </div>
    ),
  },
];

export const tradingFaq = [
  {
    title: "How does trading work?",
    description: (
      <>
        <p className="ml-8">
          The Secondary Market functions as a private bulletin board, where
          investors manually match buy and sell orders. It operates similarly to
          an Over-the-Counter (OTC) market.
        </p>

        <p className="ml-8">
          Please note: Due to limited liquidity and deal flow, trades may take
          longer to match than on a traditional exchange.
        </p>
      </>
    ),
  },
  {
    title: "What is the minimum order amount?",
    description: (
      <p className="ml-8">
        The minimum trade size is <strong>$10 USD.</strong>
      </p>
    ),
  },
  {
    title: "How do I start investing?",
    description: (
      <>
        <p className="ml-8">To begin:</p>
        <ol>
          <li>Complete your investor verification (Tier 3 or higher).</li>
          <li>Fund your account with USDT (Tether).</li>
          <li>Browse available listings.</li>
          <li>Create a buy order with a set price.</li>
        </ol>
      </>
    ),
  },
  {
    title: "What are the fees?",
    description: (
      <>
        <p className="ml-8">
          A 2.5% trade fee is charged on successfully completed transactions.
          This covers share transfers, contract processing, and escrow services.
        </p>

        <p className="ml-8">No fees apply to unfilled or cancelled orders.</p>
      </>
    ),
  },
  {
    title: "Why are my funds blocked when I place an order?",
    description: (
      <>
        <p className="ml-8">
          To prevent double-spending, funds are held in escrow when an order is
          placed.
        </p>

        <p className="ml-8">
          If the order is not matched, your funds are released and available to
          withdraw or use for other investments.
        </p>
      </>
    ),
  },
];

export const shareOwnershipText =
  "When you invest in private equity through our platform, you're not buying shares directly in the underlying company—you’re acquiring shares in a Segregated Portfolio (SP) within a segregated structure called an SPC that is backed by shares in the underlying company. This setup gives you indirect ownership, legal asset segregation, and access to private Bitcoin companies. In this section, we explain exactly how it works, the safeguards in place, and what that means for you as an investor.";

export const shareOwnershipFaq = [
  {
    title: "Disclaimer: Understanding the SPC & Investment Structure",
    description: (
      <div>
        <p className="ml-8">
          Bnk To The Future Capital SPC is a Special Purpose Company registered
          in the Cayman Islands, operating under a Segregated Portfolio Company
          (SPC) structure. Company Number is 298427. The registered address is
          Willow House, Cricket Square, KY1-1001, Cayman Islands.
        </p>
        <p className="ml-8">
          The SPC creates one Segregated Portfolio (SP) per pitch to hold, on a
          pass-through basis, the underlying securities of companies that raised
          funds via the platform.
        </p>
        <p className="ml-8">
          Neither the BF Securities Broker nor the Platform Operator holds any
          of these assets. These are not client assets held by the broker, and
          are therefore not protected as client assets. Instead they are
          segregated.
        </p>
      </div>
    ),
  },
  {
    title: "Key Things to Know",
    description: (
      <div>
        <p className="ml-8">
          The SPC creates a specific SP for each company raising funds on the
          Platform.
        </p>
        <p className="ml-8">
          Investors do not hold direct shares in the underlying companies.
        </p>
        <p className="ml-8">
          Instead, Investors own shares in a specific SP, which mirrors the
          ownership of the underlying company on a 1:1 basis.
        </p>
        <p className="ml-8">
          This is not a fund. Each share that an investor holds in the SPV
          directly mirrors, in quantity and value, the underlying asset held by
          the SPV.
        </p>
        <p className="ml-8">
          SP shares may be acquired via the primary market (when the deal is
          first listed) or the secondary market (from other investors).
        </p>
        <p className="ml-8">
          Information about the Investee Entity may not be equally or adequately
          available to all investors. All investors, whether primary or
          secondary market purchasers, are expected to conduct their own due
          diligence.
        </p>
        <p className="ml-8">
          This is not investment advice. Bnk To The Future does not provide
          recommendations on whether to invest or not.
        </p>
      </div>
    ),
  },
  {
    title: "Asset Protection Through SP Segregation",
    description: (
      <div>
        <p className="ml-8">
          A key feature of the SPC is that the assets and liabilities of each SP
          are segregated from the assets and liabilities of other SPs. This
          means that if one SP faces financial difficulties, the assets in other
          SPs are protected.
        </p>
      </div>
    ),
  },
  {
    title: "Disclaimer",
    description: (
      <div>
        <p className="ml-8">
          The information provided here is for general knowledge and
          informational purposes only. Investing in private equity carries
          significant risk, and investors should conduct their own research and
          consult with a qualified financial advisor before making any
          investment decisions. Please review our risk warnings{" "}
          <a
            href="https://home.bnktothefuture.com/risk-warnings/"
            className="underline font-semibold"
          >
            here.
          </a>
        </p>
      </div>
    ),
  },

  {
    title: "How our investors own indirect shares of Bitcoin companies",
    description: (
      <div>
        <OurInvestorsImage className="scale-90" />
      </div>
    ),
  },
  {
    title: "How you get exposure to private Bitcoin companies (e.g. Kraken)",
    description: (
      <div>
        <ExposureImage className="scale-95" />
      </div>
    ),
  },
];
